// Copyright (c) 2025 Goffy. All Rights Reserved. This file belongs to Goffy.

import { useMemo, useState, useEffect, useRef } from 'react';
import { Pressable, ScrollView, StyleSheet } from 'react-native';
import { Redirect, useLocalSearchParams, useRouter, useRootNavigationState } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { Text, View } from '@/components/Themed';
import { createAppLogger } from '@/services/appLogger';
import { getSharedBffWebSocket, TeamCreatedEvent } from '@/services/bffWebSocket';
import { setCurrentTourId } from '@/services/currentTour';
import { setCurrentHole, setCurrentRoundId } from '@/services/currentRound';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { useRoundState } from '@/stores/roundState';

type Player = { id: string; name: string; createdAt: string };
type Team = { name: string; players: Player[]; requestId: string };

export default function TeamDrawScreen() {
  const { debug: debugParam, roundId: roundIdParam } = useLocalSearchParams<{ debug?: string; roundId?: string }>();
  const storedRoundId = useRoundState((state) => state.roundId);
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [players, setPlayers] = useState<Player[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState<string>('Skapar lag slumpmässigt...');
  const [skipTeamDraw, setSkipTeamDraw] = useState(false);
  const [courseName, setCourseName] = useState<string | null>(null);
  const [tourName, setTourName] = useState<string | null>(null);
  const [tourId, setTourId] = useState<string | null>(null);
  const debug = useMemo(() => debugParam === 'true', [debugParam]);
  const normalizedRoundId = useMemo(() => {
    const raw = Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam;
    return raw ?? null;
  }, [roundIdParam]);
  const roundId = normalizedRoundId ?? storedRoundId;
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'team-draw',
      }),
    [apiBaseUrl, user?.token]
  );
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const autoSingleTeamsRef = useRef(false);

  useEffect(() => {
    if (tourId) {
      setCurrentTourId(tourId);
    }
  }, [tourId]);

  useEffect(() => {
    if (roundId) {
      setCurrentRoundId(roundId);
    }
  }, [roundId]);

  useEffect(() => {
    if (!roundId || !user?.token || !isNavigationReady) return;
    let cancelled = false;
    const fetchTeams = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/teams?roundId=${roundId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const data = await response.json();
        const incomingTeams = Array.isArray(data) ? data : (data?.teams ?? data?.Teams ?? []);
        if (!cancelled && incomingTeams.length > 0) {
          setSkipTeamDraw(true);
          setCurrentHole(1);
          const params: string[] = [];
          if (debug) params.push('debug=true');
          const query = params.length ? `?${params.join('&')}` : '';
          router.replace(`/round/${roundId}/hole/1/compact${query}`);
        }
      } catch {
        // Ignore; fallback to draw flow.
      }
    };
    fetchTeams();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, debug, isNavigationReady, roundId, router, user?.token]);

  useEffect(() => {
    if (!roundId || !user?.token || !isNavigationReady) return;
    let cancelled = false;
    const fetchRound = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const data = (await response.json()) as {
          courseName?: string;
          CourseName?: string;
          tourId?: string;
          TourId?: string;
        };
        if (cancelled) return;
        const incomingCourse = data.courseName ?? data.CourseName ?? null;
        const incomingTourId = data.tourId ?? data.TourId ?? null;
        if (incomingCourse) setCourseName(incomingCourse);
        if (incomingTourId) setTourId(incomingTourId);
      } catch {
        // Ignore; fallback to ids.
      }
    };
    fetchRound();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, isNavigationReady, roundId, user?.token]);

  useEffect(() => {
    if (!tourId || !user?.token || !isNavigationReady) return;
    let cancelled = false;
    const fetchTour = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/tours/${tourId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const data = (await response.json()) as { tourName?: string; TourName?: string; name?: string; Name?: string };
        if (cancelled) return;
        const incomingTourName = data.tourName ?? data.TourName ?? data.name ?? data.Name ?? null;
        if (incomingTourName) setTourName(incomingTourName);
      } catch {
        // Ignore; fallback to ids.
      }
    };
    fetchTour();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, isNavigationReady, tourId, user?.token]);

  useEffect(() => {
    if (!isNavigationReady || !isFocused) return;
    socketRef.current.setSubscriptionContext({ roundId });
  }, [isFocused, isNavigationReady, roundId]);

  useEffect(() => {
    if (!isNavigationReady) return;
    if (skipTeamDraw) return;
    const loadPlayers = async () => {
      try {
        if (!user?.token || !roundId) {
          return;
        }
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}/players`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          throw new Error('Failed to load players');
        }
        const data = (await response.json()) as { players?: Player[]; Players?: Player[] };
        const incomingPlayers = data.players ?? data.Players ?? [];
        setPlayers(incomingPlayers);
        randomize(incomingPlayers);
        setError(null);
        setStatus(`Hittade ${incomingPlayers.length} spelare.`);
      } catch {
        setError('Kunde inte hämta spelare.');
      }
    };
    loadPlayers();
  }, [apiBaseUrl, user?.token, isNavigationReady, roundId, skipTeamDraw]);

  useEffect(() => {
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    const unsubscribeMessages = socket.onMessage((message) => {
      if ((message as TeamCreatedEvent).type === 'team.created') {
        const payload = message as TeamCreatedEvent;
        if (payload.requestId) {
          setSavedIds((prev) => {
            const next = new Set(prev);
            next.add(payload.requestId!);
            return next;
          });
          setStatus('Lag sparat, fortsätter...');
          logToBff('team.created.received', { requestId: payload.requestId, teamId: payload.teamId });
        } else {
          logToBff('team.created.received.missing_request_id', { teamId: payload.teamId });
        }
      } else if ((message as { type?: string }).type === 'team.initiate.ack') {
        const ack = message as { requestId?: string; teamName?: string; roundId?: string };
        if (ack.requestId) {
          logToBff('team.initiate.ack', { requestId: ack.requestId, teamName: ack.teamName, roundId: ack.roundId });
        }
      }
    });
    const unsubscribeConnection = socket.onConnectionChange((state) => {
      if (state === 'connected') {
        setError(null);
      }
    });
    return () => {
      unsubscribeMessages();
      unsubscribeConnection();
    };
  }, [user?.token, logToBff]);

  useEffect(() => {
    if (teams.length > 0 && savedIds.size >= teams.length && roundId) {
      setCurrentHole(1);
      const params: string[] = [];
      if (debug) params.push('debug=true');
      const query = params.length ? `?${params.join('&')}` : '';
      router.replace(`/round/${roundId}/hole/1/compact${query}`);
    }
  }, [savedIds, teams, roundId, debug, router, tourId]);

  const randomize = (playersList: Player[]) => {
    const shuffled = [...playersList].sort(() => Math.random() - 0.5);
    const grouped: Team[] = [];
    for (let i = 0; i < shuffled.length; i += 2) {
      const slice = shuffled.slice(i, i + 2);
      grouped.push({
        name: `Lag ${grouped.length + 1}`,
        players: slice,
        requestId: generateId(),
      });
    }
    setTeams(grouped);
    setSavedIds(new Set());
    setStatus('Lag genererade. Spara dem för att fortsätta.');
  };

  const saveTeams = (teamsOverride?: Team[] | unknown) => {
    const teamsToSave = Array.isArray(teamsOverride) ? teamsOverride : teams;
    if (!Array.isArray(teamsToSave) || teamsToSave.length === 0) return;
    if (!roundId) {
      setError('Saknar roundId – kan inte spara lag.');
      return;
    }
    setSaving(true);
    const socket = socketRef.current;
    const payload = {
      type: 'team.initiate',
      roundId,
      teams: teamsToSave.map((t) => ({
        name: t.name,
        playerIds: t.players.map((p) => p.id),
        requestId: t.requestId,
      })),
    };
    const sent = socket.send(payload);
    if (!sent) {
      setStatus('Väntar på websocket-anslutning...');
      logToBff('team.initiate.queued', { teamCount: teamsToSave.length });
    } else {
      setStatus('Skickar lag till BFF...');
      logToBff('team.initiate.sent', { teamCount: teamsToSave.length });
    }
    setSaving(false);
  };

  useEffect(() => {
    if (skipTeamDraw) return;
    if (autoSingleTeamsRef.current) return;
    if (players.length === 0) return;
    if (players.length >= 4) return;
    autoSingleTeamsRef.current = true;
    const singleTeams = players.map((player, idx) => ({
      name: `Lag ${idx + 1}`,
      players: [player],
      requestId: generateId(),
    }));
    setTeams(singleTeams);
    setSavedIds(new Set());
    setStatus('Lag genererade. Spara dem för att fortsätta.');
    saveTeams(singleTeams);
  }, [players, skipTeamDraw]);

  if (!isNavigationReady) return null;
  if (!user) return <Redirect href="/login" />;

  return (
    <ScrollView contentContainerStyle={styles.container} style={styles.scroll}>
      <Text style={styles.hero}>👥</Text>
      <Text style={styles.title}>Lottade lag</Text>
      <View style={styles.statusCard}>
        <Text style={styles.statusText}>{status}</Text>
        {courseName ? <Text style={styles.statusText}>Bana: {courseName}</Text> : roundId ? <Text style={styles.statusText}>Runda: {roundId}</Text> : null}
        {tourName ? <Text style={styles.statusText}>Tour: {tourName}</Text> : tourId ? <Text style={styles.statusText}>Tour: {tourId}</Text> : null}
      </View>
      {error ? <Text style={styles.error}>{error}</Text> : null}

      <View style={styles.card}>
        {teams.map((team) => (
          <View key={team.requestId} style={styles.teamCard}>
            <View style={styles.teamHeader}>
              <Text style={styles.teamName}>{team.name}</Text>
              {savedIds.has(team.requestId) ? <Text style={styles.badge}>sparat</Text> : null}
            </View>
            {team.players.map((p) => (
              <Text key={p.id} style={styles.playerName}>
                {p.name}
              </Text>
            ))}
            {team.players.length === 1 ? <Text style={styles.playerName}>ledig plats</Text> : null}
          </View>
        ))}
      </View>

      <View style={styles.buttonRow}>
        <Pressable style={[styles.secondaryButton]} onPress={() => randomize(players)}>
          <Text style={styles.secondaryButtonText}>Slumpa om</Text>
        </Pressable>
        <Pressable style={[styles.primaryButton, saving && styles.primaryButtonDisabled]} onPress={saveTeams} disabled={saving}>
          <Text style={styles.primaryButtonText}>{saving ? 'Sparar...' : 'Spara lag'}</Text>
        </Pressable>
      </View>

      <Pressable
        style={[styles.linkButton]}
        onPress={() => {
          setCurrentHole(1);
          const params: string[] = [];
          if (debug) params.push('debug=true');
          if (tourId) params.push(`tourId=${tourId}`);
          const query = params.length ? `?${params.join('&')}` : '';
          if (!roundId) return;
          router.replace(`/round/${roundId}/hole/1/compact${query}`);
        }}
      >
        <Text style={styles.linkButtonText}>Hoppa över</Text>
      </Pressable>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    gap: 12,
  },
  scroll: {
    flex: 1,
    backgroundColor: '#040814',
  },
  hero: {
    fontSize: 48,
    textAlign: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    color: '#e5edff',
  },
  statusCard: {
    padding: 12,
    borderRadius: 10,
    backgroundColor: '#0f1c3c',
    borderWidth: 1,
    borderColor: '#24346b',
  },
  statusText: {
    color: '#d5e1ff',
  },
  error: {
    color: '#f87171',
    textAlign: 'center',
  },
  card: {
    padding: 12,
    borderRadius: 12,
    backgroundColor: '#0b1224',
    borderWidth: 1,
    borderColor: '#1c2c5a',
    gap: 10,
  },
  teamCard: {
    padding: 10,
    borderRadius: 10,
    backgroundColor: '#0f1c3c',
    borderWidth: 1,
    borderColor: '#24346b',
    gap: 4,
  },
  teamHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  teamName: {
    fontWeight: '700',
    color: '#e5edff',
  },
  badge: {
    backgroundColor: '#34d399',
    color: '#041026',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    fontWeight: '700',
  },
  playerName: {
    color: '#d5e1ff',
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 12,
  },
  primaryButton: {
    flex: 1,
    backgroundColor: '#2563eb',
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  primaryButtonDisabled: {
    opacity: 0.6,
  },
  primaryButtonText: {
    color: '#e5edff',
    fontWeight: '700',
  },
  secondaryButton: {
    flex: 1,
    backgroundColor: '#0f172a',
    borderWidth: 1,
    borderColor: '#24346b',
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  secondaryButtonText: {
    color: '#e5edff',
    fontWeight: '700',
  },
  linkButton: {
    alignItems: 'center',
    paddingVertical: 10,
  },
  linkButtonText: {
    color: '#9ca3af',
    textDecorationLine: 'underline',
  },
});

function generateId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    const uuid = (crypto as { randomUUID?: () => string }).randomUUID?.();
    if (uuid) return uuid;
  }
  const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
